const normalizePath = (path: string) => {
    const normalized = path.replace(/\/+$/, '');
    return normalized || '/';
};

const getHashTarget = (hash: string) => {
    const rawId = hash.replace(/^#/, '');
    if (!rawId) return null;

    let id = rawId;
    try {
        id = decodeURIComponent(rawId);
    } catch {
        id = rawId;
    }

    return document.getElementById(id);
};

const getSamePageHashTarget = (link: HTMLAnchorElement) => {
    if (!link.hash || link.hash === '#') return null;

    const url = new URL(link.href, window.location.href);
    const currentPath = normalizePath(window.location.pathname);
    const linkPath = normalizePath(url.pathname);
    const samePage =
        url.origin === window.location.origin &&
        linkPath === currentPath &&
        (url.search === window.location.search || url.search === '');

    if (!samePage) return null;

    const target = getHashTarget(url.hash);
    return target ? { target, hash: url.hash } : null;
};

const smoothScrollToTarget = (target: HTMLElement) => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const style = window.getComputedStyle(target);
    const scrollMarginTop = parseFloat(style.scrollMarginTop) || 0;
    const top = target.getBoundingClientRect().top + window.scrollY - scrollMarginTop;

    window.scrollTo({
        top,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
};

const MOBILE_GLASS_CLASS = 'is-style-shields-glass-strong';
const HEADER_SELECTOR = '.header--sidekick';
const HEADER_MOBILE_CLASS = 'has-sgb-mobile-menu';
const HEADER_OPEN_CLASS = 'has-sgb-mobile-menu-open';
const HEADER_PADDING_VAR = '--sgb-mobile-menu-header-padding-right';
const SCROLLBAR_VAR = '--sgb-mobile-menu-scrollbar-width';

document.addEventListener('DOMContentLoaded', () => {
    const navMenus = document.querySelectorAll('.sgb-nav-menu') as NodeListOf<HTMLElement>;

    navMenus.forEach((navWrapper) => {
        const orientation = navWrapper.getAttribute('data-orientation');
        const navInner = navWrapper.querySelector('.sgb-nav-menu__inner') as HTMLElement;
        const overlay = navWrapper.querySelector('.sgb-nav-menu__overlay');
        const toggleBtn = navWrapper.querySelector('.sgb-nav-menu__toggle');
        const header = (navWrapper.closest(HEADER_SELECTOR) || document.querySelector(HEADER_SELECTOR)) as HTMLElement | null;

        if (!navInner) return;

        const setHeaderMobileMode = (isMobile: boolean) => {
            if (!header) return;

            if (isMobile) {
                // Capture the header's original padding-right BEFORE the
                // mobile class is applied (the class overrides padding-right
                // via calc(--sgb-mobile-menu-header-padding-right + ...)).
                if (!header.classList.contains(HEADER_MOBILE_CLASS)) {
                    const paddingRight = window.getComputedStyle(header).paddingRight || '0px';
                    header.style.setProperty(HEADER_PADDING_VAR, paddingRight);
                }
                header.classList.add(HEADER_MOBILE_CLASS);
            } else {
                header.classList.remove(HEADER_MOBILE_CLASS);
                header.classList.remove(HEADER_OPEN_CLASS);
                header.style.removeProperty(HEADER_PADDING_VAR);
            }
        };

        const setHeaderOpenMode = (isOpen: boolean) => {
            header?.classList.toggle(HEADER_OPEN_CLASS, isOpen);
        };

        const setMobileMode = (isMobile: boolean) => {
            navWrapper.classList.toggle('is-mobile-menu', isMobile);
            overlay?.classList.toggle(MOBILE_GLASS_CLASS, isMobile);
            setHeaderMobileMode(isMobile);
        };

        const closeMenu = () => {
            navWrapper.classList.remove('is-open');
            setHeaderOpenMode(false);
            if (toggleBtn) {
                toggleBtn.setAttribute('aria-expanded', 'false');
                toggleBtn.setAttribute('aria-label', 'Open menu');
            }
            document.body.style.overflow = ''; // Restore background scrolling
            document.body.style.paddingRight = '';
            document.documentElement.style.removeProperty(SCROLLBAR_VAR);
        };

        const navLinks = navInner.querySelectorAll<HTMLAnchorElement>('a[href]');

        navLinks.forEach((link) => {
            link.addEventListener('click', (event) => {
                if (
                    event.defaultPrevented ||
                    event.button !== 0 ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey ||
                    (link.target && link.target !== '_self')
                ) {
                    return;
                }

                const anchorTarget = getSamePageHashTarget(link);

                if (anchorTarget) {
                    event.preventDefault();
                    closeMenu();

                    if (window.location.hash !== anchorTarget.hash) {
                        window.history.pushState(null, '', anchorTarget.hash);
                    }

                    requestAnimationFrame(() => smoothScrollToTarget(anchorTarget.target));
                    return;
                }

                if (navWrapper.classList.contains('is-open')) {
                    closeMenu();
                }
            });
        });

        if (orientation === 'vertical') {
            setMobileMode(false);
            navWrapper.classList.remove('is-open');
            navWrapper.classList.add('is-initialized');
            return;
        }

        const parent = navWrapper.parentElement;
        if (!parent) return;

        const BUFFER = 40;
        let requiredDesktopWidth = 0;
        let latestAvailableWidth = parent.getBoundingClientRect().width;

        // --- Interaction Logic ---

        const openMenu = () => {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            if (scrollbarWidth > 0) {
                document.documentElement.style.setProperty(SCROLLBAR_VAR, `${scrollbarWidth}px`);
                document.body.style.paddingRight = `${scrollbarWidth}px`;
            }
            document.body.style.overflow = 'hidden'; // Prevent background scrolling
            navWrapper.classList.add('is-open');
            setHeaderOpenMode(true);
            if (toggleBtn) {
                toggleBtn.setAttribute('aria-expanded', 'true');
                toggleBtn.setAttribute('aria-label', 'Close menu');
            }
        };

        const toggleMenu = () => {
            if (navWrapper.classList.contains('is-open')) {
                closeMenu();
                return;
            }

            openMenu();
        };

        if (toggleBtn) toggleBtn.addEventListener('click', toggleMenu);

        document.addEventListener('click', (event) => {
            if (!navWrapper.classList.contains('is-open')) return;
            if (!navWrapper.classList.contains('is-mobile-menu')) return;

            const target = event.target as Node | null;
            if (!target) return;
            if (overlay?.contains(target)) return;
            if (toggleBtn?.contains(target)) return;

            closeMenu();
        });

        // --- Collision Logic ---

        const getOuterWidth = (el: Element): number => {
            const style = window.getComputedStyle(el);

            if (style.display === 'none' || style.position === 'absolute' || style.position === 'fixed') {
                return 0;
            }

            const ml = parseFloat(style.marginLeft) || 0;
            const mr = parseFloat(style.marginRight) || 0;
            const rect = (el as HTMLElement).getBoundingClientRect();
            const scrollWidth = (el as HTMLElement).scrollWidth || 0;

            return Math.max(rect.width, scrollWidth) + ml + mr;
        };

        const measureDesktopLayout = (measure: () => number): number => {
            const wasMobile = navWrapper.classList.contains('is-mobile-menu');
            const previousVisibility = navWrapper.style.visibility;
            const previousPointerEvents = navWrapper.style.pointerEvents;
            const previousFlexWrap = navInner.style.flexWrap;
            const previousWidth = navInner.style.width;
            const previousMaxWidth = navInner.style.maxWidth;

            navWrapper.style.visibility = 'hidden';
            navWrapper.style.pointerEvents = 'none';
            setMobileMode(false);
            navInner.style.flexWrap = 'nowrap';
            navInner.style.width = 'max-content';
            navInner.style.maxWidth = 'none';

            const width = measure();

            navInner.style.flexWrap = previousFlexWrap;
            navInner.style.width = previousWidth;
            navInner.style.maxWidth = previousMaxWidth;
            navWrapper.style.visibility = previousVisibility;
            navWrapper.style.pointerEvents = previousPointerEvents;

            if (wasMobile) {
                setMobileMode(true);
            }

            return width;
        };

        const calculateRequiredWidth = () => {
            return measureDesktopLayout(() => {
                let totalSiblingWidth = 0;
                let visibleSiblings = 0;

                Array.from(parent.children).forEach((child) => {
                    if (child === navWrapper) return;

                    const width = getOuterWidth(child);
                    if (width > 0) {
                        totalSiblingWidth += width;
                        visibleSiblings++;
                    }
                });

                if (window.getComputedStyle(navInner).display === 'none') {
                    return 0;
                }

                const parentStyle = window.getComputedStyle(parent);
                const gap = parseFloat(parentStyle.columnGap) || parseFloat(parentStyle.gap) || 0;
                const totalGapWidth = gap * visibleSiblings;
                return Math.max(
                    navInner.scrollWidth,
                    getOuterWidth(navInner)
                ) + totalSiblingWidth + totalGapWidth + BUFFER;
            });
        };

        const updateLayout = (availableWidth = latestAvailableWidth) => {
            latestAvailableWidth = availableWidth;

            if (
                navWrapper.classList.contains('is-open') &&
                navWrapper.classList.contains('is-mobile-menu') &&
                requiredDesktopWidth > 0
            ) {
                if (availableWidth >= requiredDesktopWidth) {
                    closeMenu();
                    setMobileMode(false);
                }

                return;
            }

            requiredDesktopWidth = calculateRequiredWidth();

            if (availableWidth < requiredDesktopWidth && requiredDesktopWidth > 0) {
                setMobileMode(true);
            } else {
                if (navWrapper.classList.contains('is-open')) {
                    closeMenu();
                }
                setMobileMode(false);
            }

            // NEW: Reveal the menu now that the layout is locked in
            if (!navWrapper.classList.contains('is-initialized')) {
                // requestAnimationFrame ensures the browser applies the mobile/desktop
                // classes to the DOM *before* turning the opacity back on.
                requestAnimationFrame(() => {
                    navWrapper.classList.add('is-initialized');
                });
            }
        };

        const observer = new ResizeObserver((entries) => {
            updateLayout(entries[0].contentRect.width);
        });

        observer.observe(parent);

        requestAnimationFrame(() => updateLayout());
        (document as any).fonts?.ready?.then(() => updateLayout());
    });
});
